/**
 * PlaybookSocialShare — one-click share row for the "you just downloaded" state.
 *
 * Renders X / Facebook / LinkedIn share buttons plus a copy-link button.
 * Every share URL is a real, working intent URL (no OAuth needed) so users
 * can amplify the playbook straight to their audience.
 *
 * Design intent: this component turns every download into a marketing
 * amplifier. Keep the copy tight, action-focused, and TCPA-safe (no claims,
 * no pressure, just "I just grabbed this").
 *
 * Props:
 *   - playbookTitle   ← e.g. "The 90-Second Call Coach"
 *   - shareUrl        ← the URL you want people to visit when they click through
 *                        (usually the landing page for this playbook)
 *   - onShare?        ← optional callback fired with the platform label so the
 *                        parent can `track()` an analytics event
 *   - compact?        ← when true, renders as a single row instead of stacked
 */
import { useState } from "react";
import { Twitter, Facebook, Linkedin, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";

export default function PlaybookSocialShare({
  playbookTitle,
  shareUrl,
  onShare,
  compact = false,
  onLightBg = compact, // compact defaults to light; expandable mode assumes dark hero
}) {
  const [copied, setCopied] = useState(false);

  const url = shareUrl || (typeof window !== "undefined" ? window.location.href : "");
  const copy = `Just downloaded ${playbookTitle} from @iSpeedToClose — free wholesaling playbook 👇`;
  const encodedUrl = encodeURIComponent(url);
  const encodedCopy = encodeURIComponent(copy);

  const targets = [
    {
      key: "x",
      label: "X",
      icon: Twitter,
      href: `https://x.com/intent/tweet?text=${encodedCopy}&url=${encodedUrl}`,
      bg: "#000",
      fg: "#fff",
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedCopy}`,
      bg: "#1877F2",
      fg: "#fff",
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      bg: "#0A66C2",
      fg: "#fff",
    },
  ];

  const handleClick = (platform) => {
    try { onShare && onShare(platform); } catch { /* no-op */ }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste anywhere!");
      handleClick("copy_link");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy the URL manually.");
    }
  };

  return (
    <div
      className={`${compact ? "flex flex-wrap items-center gap-2" : "mt-4 space-y-2.5"}`}
      data-testid="playbook-social-share"
    >
      {!compact && (
        <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">
          Help another wholesaler find this 👇
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {targets.map(({ key, label, icon: Icon, href, bg, fg }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleClick(key)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition hover:opacity-90 hover:-translate-y-0.5"
            style={{ background: bg, color: fg }}
            data-testid={`playbook-share-${key}`}
            aria-label={`Share on ${label}`}
          >
            <Icon size={13} />
            {label}
          </a>
        ))}
        <button
          type="button"
          onClick={copyLink}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition ${
            onLightBg
              ? "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200"
              : "bg-white/10 text-white hover:bg-white/20"
          }`}
          data-testid="playbook-share-copy"
          aria-label="Copy link"
        >
          {copied ? <Check size={13} /> : <LinkIcon size={13} />}
          {copied ? "Copied!" : "Copy link"}
        </button>
      </div>
    </div>
  );
}
